import * as React from 'react';
import {
  CalendarCheckIcon,
  CalendarDaysIcon,
  CheckCircle2Icon,
  LogInIcon,
  MapPinIcon,
  SparklesIcon,
  UsersIcon
} from 'lucide-react';

import { appIdentity } from '@/app/app-config';
import { useSession } from '@/components/auth/user-provider';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';

const HOW_IT_WORKS = [
  {
    step: 1,
    title: 'Choose a space',
    description: 'Pick a desk, meeting room, car park, or other resource types from live inventory.',
    icon: MapPinIcon
  },
  {
    step: 2,
    title: 'Pick a time',
    description: 'Use quick time windows or choose exact start and end times for your visit.',
    icon: CalendarDaysIcon
  },
  {
    step: 3,
    title: 'Confirm & manage',
    description: 'Book in a click, then review, check in, or cancel from My bookings anytime.',
    icon: CheckCircle2Icon
  }
] as const;

const VALUE_POINTS = [
  {
    title: 'Find what fits',
    description: 'Explore shared desks, rooms and parking, then choose a place that suits your day.',
    icon: CalendarCheckIcon
  },
  {
    title: 'Plan together',
    description: 'Coordinate office days with your team while respecting each person’s sharing choices.',
    icon: UsersIcon
  },
  {
    title: 'Stay in control',
    description: 'Manage your bookings and ask for help finding options; you confirm proposed changes.',
    icon: SparklesIcon
  }
] as const;

/**
 * Branded signed-out welcome. Presentation only — no authenticated API calls.
 * Sign-in reuses the session login path with busy/double-click protection.
 */
export function WelcomeLanding() {
  const { signIn } = useSession();
  const [busy, setBusy] = React.useState(false);
  const [signInError, setSignInError] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = `Welcome · ${appIdentity.name}`;
    return () => {
      document.title = appIdentity.name;
    };
  }, []);

  const handleSignIn = () => {
    if (busy) return;
    setBusy(true);
    setSignInError(null);
    try {
      // Synchronous open kept on the user-gesture stack for iframe → top-level handoff.
      // login() is void and swallows MSAL promise rejects internally; guard sync throws.
      signIn();
    } catch (err) {
      setSignInError(
        err instanceof Error ? err.message : 'Sign-in could not start. Please try again.'
      );
      setBusy(false);
      return;
    }
    // Keep the button disabled briefly so double-clicks cannot start
    // overlapping MSAL interactions (a common freezes/crash cause).
    window.setTimeout(() => setBusy(false), 1500);
  };

  return (
    <div className="bg-background flex min-h-svh flex-col">
      <a
        href="#welcome-main"
        className="bg-background text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
      >
        Skip to main content
      </a>

      <header className="border-border/60 bg-background/80 sticky top-0 z-10 border-b backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            <img src={appIdentity.iconPath} alt="" className="size-9 shrink-0 rounded-lg" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight">
                {appIdentity.name}
              </p>
              <p className="text-muted-foreground hidden truncate text-xs sm:block">
                Make more of the places we share
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ThemeToggle />
            <Button
              type="button"
              size="sm"
              onClick={handleSignIn}
              disabled={busy}
              className="gap-1.5"
            >
              <LogInIcon className="size-3.5" aria-hidden />
              {busy ? 'Opening sign-in…' : 'Sign in'}
            </Button>
          </div>
        </div>
      </header>

      <main
        id="welcome-main"
        tabIndex={-1}
        className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-10 px-4 py-8 outline-none sm:px-6 sm:py-12"
      >
        {signInError ? (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-4 py-3 text-sm"
          >
            {signInError}
          </div>
        ) : null}

        <section
          aria-labelledby="welcome-heading"
          className="from-primary/10 via-background to-background relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 sm:p-10"
        >
          <div
            className="bg-primary/5 pointer-events-none absolute -top-16 -right-10 size-56 rounded-full blur-3xl"
            aria-hidden
          />
          <div
            className="bg-primary/10 pointer-events-none absolute -bottom-20 -left-10 size-48 rounded-full blur-3xl"
            aria-hidden
          />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl space-y-4">
              <Badge variant="secondary" className="w-fit">
                Shared places, better used
              </Badge>
              <h1
                id="welcome-heading"
                className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl"
              >
                {appIdentity.tagline}
              </h1>
              <p className="text-muted-foreground max-w-xl text-base sm:text-lg">
                {appIdentity.description}
              </p>
              <ul className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2Icon className="text-primary size-3.5 shrink-0" aria-hidden />
                  Find shared spaces
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2Icon className="text-primary size-3.5 shrink-0" aria-hidden />
                  Plan time together
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2Icon className="text-primary size-3.5 shrink-0" aria-hidden />
                  Manage what you use
                </li>
              </ul>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-3 lg:w-auto">
              <Button
                type="button"
                size="lg"
                className="w-full gap-2"
                onClick={handleSignIn}
                disabled={busy}
              >
                <LogInIcon className="size-4" aria-hidden />
                {busy ? 'Opening sign-in…' : 'Sign in'}
              </Button>
              <p className="text-muted-foreground text-center text-xs sm:text-left">
                Use your work account. After sign-in you land on the guided booking home.
              </p>
            </div>
          </div>
        </section>

        <section aria-labelledby="purpose-heading" className="bg-muted/40 rounded-2xl border p-6 sm:p-8">
          <div className="max-w-3xl space-y-3">
            <p className="text-primary text-xs font-semibold tracking-wide uppercase">Why Commons Foundry</p>
            <h2 id="purpose-heading" className="text-xl font-semibold tracking-tight sm:text-2xl">
              Better use starts with a clearer view of what we share.
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
              A desk for a focused day, a room for a team meeting, or a place to park: shared resources work better when people can find them, plan around each other, and manage their use in one place. Commons Foundry brings those everyday decisions together.
            </p>
          </div>
        </section>

        <section aria-labelledby="how-heading" className="space-y-4">
          <div className="max-w-2xl space-y-1">
            <h2 id="how-heading" className="text-lg font-semibold tracking-tight sm:text-xl">
              How booking works
            </h2>
            <p className="text-muted-foreground text-sm">
              A short path from intent to a confirmed reservation — available after you sign in.
            </p>
          </div>
          <ol className="grid gap-3 sm:grid-cols-3">
            {HOW_IT_WORKS.map((item) => (
              <li key={item.step}>
                <Card className="h-full">
                  <CardHeader className="space-y-3 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-lg">
                        <item.icon className="size-5" aria-hidden />
                      </span>
                      <Badge variant="outline" className="tabular-nums">
                        Step {item.step}
                      </Badge>
                    </div>
                    <CardTitle className="text-base">{item.title}</CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="value-heading" className="space-y-4">
          <div className="max-w-2xl space-y-1">
            <h2 id="value-heading" className="text-lg font-semibold tracking-tight sm:text-xl">
              Make more of shared places
            </h2>
            <p className="text-muted-foreground text-sm">
              Find resources, align plans and manage the places you use after sign-in.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {VALUE_POINTS.map((item) => (
              <Card
                key={item.title}
                className="hover:border-primary/30 transition-colors"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <item.icon className="text-muted-foreground size-4" aria-hidden />
                    <CardTitle className="text-base">{item.title}</CardTitle>
                  </div>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="ai-heading"
          className="from-primary/5 via-background to-background rounded-2xl border bg-gradient-to-br p-6 sm:p-8"
        >
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl space-y-2">
              <div className="flex items-center gap-2">
                <span className="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-lg">
                  <SparklesIcon className="size-5" aria-hidden />
                </span>
                <h2 id="ai-heading" className="text-lg font-semibold tracking-tight">
                  Prefer to ask in plain language?
                </h2>
              </div>
              <p className="text-muted-foreground text-sm sm:text-base">
                After you sign in, the AI booking assistant can search availability and propose a
                booking you approve — nothing is reserved until you confirm.
              </p>
            </div>
            <Button
              type="button"
              size="lg"
              variant="outline"
              className="w-full shrink-0 gap-2 sm:w-auto"
              onClick={handleSignIn}
              disabled={busy}
            >
              <LogInIcon className="size-4" aria-hidden />
              {busy ? 'Opening sign-in…' : 'Sign in to try the assistant'}
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-border/60 border-t">
        <div className="text-muted-foreground mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-xs sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>{appIdentity.name}</p>
          <p>Bookings require an organisation account. No spaces are listed until you sign in.</p>
        </div>
      </footer>
    </div>
  );
}
